import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { latestDealRecordQueryOptions } from '@lib/query/options/payment';
import { getFirstDayOfCurrentMonthDate } from '@lib/util/date';
import { convertToTwd } from '@lib/util/money';
import Tag from '@modules/common/components/tag';
import Text from '@modules/common/components/text';

/**
 * Format date to display format (MM月)
 */
const formatMonthDisplay = (date: Date): string => {
  return String(date.getMonth() + 1).padStart(2, '0');
};

/**
 * Format date to display format (YYYY-MM-DD)
 */
const formatDateDisplay = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const MonthlySettlementConfirm = () => {
  const firstDayOfCurrentMonth = useMemo(
    () => getFirstDayOfCurrentMonthDate(),
    []
  );

  const { data: dealRecord } = useQuery(
    latestDealRecordQueryOptions(
      firstDayOfCurrentMonth.toISOString()
    )
  );

  // 基礎資料
  const loanOutstanding = dealRecord?.loanOutstandingBalance ?? 0;
  const cashBalance = dealRecord?.cashBalance ?? 0;
  const rewardBalance = dealRecord?.rewardBalance ?? 0;
  const refundBalance = dealRecord?.refundBalance ?? 0;

  // 應結清金額 = 賒帳欠款 - 儲值金餘額
  // 當 cashBalance > 0 時，表示有預付貨款可抵扣
  // 當 cashBalance < 0 時，表示有額外欠款（極端情況）
  const amountDue = loanOutstanding - cashBalance;

  // 條件顯示
  const hasRewardBalance = rewardBalance !== 0;
  const hasRefundBalance = refundBalance !== 0;
  const hasPrePayment = cashBalance > 0;
  const hasAmountDue = amountDue > 0;

  // 主要顯示內容
  const renderMainContent = useMemo(() => {
    if (hasAmountDue) {
      // 標準情況：有應結清金額
      return (
        <div>
          <p>
            親愛的用戶，
            <br />
            您的帳戶截至上個月月底應結清貨款為
            <Text variant="h3-bold" as="span" className="mx-1">{convertToTwd(amountDue)}</Text>
          </p>
          <p className="text-grey-60">
            = 上月賒帳欠款 {convertToTwd(loanOutstanding)}
            {hasPrePayment && ` - 預付貨款 ${convertToTwd(cashBalance)}`}
            {cashBalance < 0 &&
              ` + 額外欠款 ${convertToTwd(Math.abs(cashBalance))}`}
          </p>
        </div>
      );
    } else {
      // 無需結清的情況
      return (
        <div>
          <p>
            親愛的用戶，
            <br />
            目前無待結清金額
          </p>
          {cashBalance > 0 && (
            <p className="text-grey-60">
              預付貨款餘額 {convertToTwd(cashBalance)}
            </p>
          )}
        </div>
      );
    }
  }, [amountDue, loanOutstanding, cashBalance, hasAmountDue, hasPrePayment]);

  return (
    <div data-testid="confirm-modal-monthly-settlement-container">
      <Text variant="title-bold" as="h3">
        {formatMonthDisplay(firstDayOfCurrentMonth)} 月份應結清貨款確認通知
      </Text>
      <Text variant="sm-regular" as="div" className="text-grey-10 mb-2 mt-2">
        {formatDateDisplay(firstDayOfCurrentMonth)}
      </Text>
      <Tag
        text="月結"
        className="bg-blue-30_op10 text-blue-30 rounded-full px-4 py-2 font-normal"
      />
      <div className="text-black-20 mt-6 flex flex-col gap-4">
        {renderMainContent}

        {/* 條件顯示：回饋金 */}
        {hasRewardBalance && (
          <p className="text-grey-60">
            回饋金餘額 {convertToTwd(rewardBalance)}
          </p>
        )}

        {/* 條件顯示：退貨回饋金 */}
        {hasRefundBalance && (
          <p className="text-grey-60">
            退貨回饋金餘額 {convertToTwd(refundBalance)}
          </p>
        )}

        <p>
          系統將在每個月一號發送應結清貨款確認通知。
          <br />
          為保障您的帳戶權益，請每月確認一次您的貨款金額。
          <br />
          若超過一個月未進行確認，系統將暫停接受新訂單。
          <br />
          請在確認金額無誤後點擊「我已閱讀並同意」按鈕。
          <br />
          感謝您的配合！
        </p>

        <p>如有任何疑問或需要協助，請洽您的業務專員或是聯繫客服。</p>
        <ul className="text-grey-60 list-disc pl-6">
          <li>客服郵箱：marxpath@marxbiotech.com</li>
          <li>客服電話：02-82455250（服務時間：週一至週五，9:00至18:00）</li>
        </ul>
      </div>
    </div>
  );
};

export default MonthlySettlementConfirm;
